import { useCallback, useEffect, useMemo, useState } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import { LoadingButton } from '@mui/lab'
import { Box, Card, CardContent, TextField, Typography } from '@mui/material'
import { trackSelfDescribingEvent } from '@snowplow/browser-tracker'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { InferType, object } from 'yup'

import { UploadFormField } from 'app/lib/components/form/UploadFormField'
import { getPlansThatContainOption, getSnowplowSchema } from 'app/lib/utils/helpers'
import { fileSchema, requiredStringSchema } from 'app/lib/utils/yupSchemas'
import { OrganizationRoute, ServiceOptionLabels } from 'app/models/scribe.models'
import { useAddActionRequestMutation, useGetOrganizationPlansQuery } from 'app/redux/scribeApi'
import { colors } from 'app/theme'

import { MemberSearchModal } from './MemberSearchModal'

export type SubmittedMember = { id: number; name: string }

const defaultValues: RequestFormData = {
  member: '',
}

const schema = object({
  member: requiredStringSchema,
  file: fileSchema,
}).required()

type RequestFormData = InferType<typeof schema>

export const RequestForm: React.FC = () => {
  const { t } = useTranslation()
  const { organizationId } = useParams() as OrganizationRoute

  const [openSearchModal, setOpenSearchModal] = useState<boolean>(false)
  const [selectedMember, setSelectedMember] = useState<SubmittedMember | null>(null)
  const { data: plans = [] } = useGetOrganizationPlansQuery({ organizationId })

  const [addActionRequest, { isLoading, isSuccess }] = useAddActionRequestMutation()

  const methods = useForm<RequestFormData>({
    resolver: yupResolver(schema),
    defaultValues,
  })

  const { formState, setValue, watch } = methods

  const selectedFile = watch('file')

  const isSubmitDisabled = !formState.isValid

  useEffect(() => {
    if (isSuccess) {
      methods.reset()
      setSelectedMember(null)
    }
  }, [isSuccess, methods, setValue])

  const idsOfWorkplaceReferralPlan = useMemo(() => {
    const filteredPlans = getPlansThatContainOption(plans, ServiceOptionLabels.WORKPLACE_REFERRAL)

    return filteredPlans.map((plan) => plan.id)
  }, [plans])

  const handleMemberSearchClick = () => {
    trackSelfDescribingEvent({
      event: {
        schema: getSnowplowSchema('button_click', '1-0-0'),
        data: {
          button_value: 'EAP Request - select member',
        },
      },
    })
    setOpenSearchModal(true)
  }

  const handleClearSelected = () => {
    methods.setValue('member', '', { shouldValidate: true })
    setSelectedMember(null)
  }

  const onClose = useCallback(() => {
    setOpenSearchModal(false)
  }, [])

  const handleSelectFile = (file: File) => {
    setValue('file', file, { shouldValidate: true })
  }

  const handleRemoveFile = () => {
    setValue('file', null, { shouldValidate: true })
  }

  const handleSelectMember = useCallback(
    (member: SubmittedMember) => {
      setSelectedMember(member)
      methods.setValue('member', member.name, { shouldValidate: true })
      onClose()
    },
    [methods, onClose],
  )

  const onSubmit = (data: RequestFormData) => {
    if (!selectedMember) {
      return
    }

    trackSelfDescribingEvent({
      event: {
        schema: getSnowplowSchema('button_click', '1-0-0'),
        data: {
          button_value: `EAP Request - request form submit - Eligibility Record Id: ${selectedMember.id}`,
        },
      },
    })

    const body = new FormData()
    body.append('document', data.file, data.file.name)

    addActionRequest({ eligibilityRecordId: selectedMember.id, body })
  }

  return (
    <Card>
      <CardContent sx={{ p: 4 }}>
        <Typography variant="h2" mb={2}>
          {t('organizationEapRequests.form.heading')}
        </Typography>
        <Typography variant="body1" mb={4} color={colors.tertiary}>
          {t('organizationEapRequests.form.description')}
        </Typography>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <Typography
              variant="caption1"
              color="dimgrey"
              sx={{ display: 'block', marginBottom: 0.5 }}
            >
              <label htmlFor="member">{t('organizationEapRequests.form.whoIsItFor')}</label>
            </Typography>
            <Box sx={{ position: 'relative', width: 325 }}>
              <Controller
                name="member"
                render={({ field }) => (
                  <TextField
                    id="member"
                    {...field}
                    onClick={handleMemberSearchClick}
                    variant="outlined"
                    size="small"
                    required
                    placeholder={t('organizationEapRequests.form.selectMember')}
                    sx={{ width: '100%' }}
                    inputProps={{
                      readOnly: true,
                      style: {
                        fontSize: '0.875em',
                      },
                      'data-testid': 'search-member-input',
                    }}
                  />
                )}
              />
              {selectedMember && (
                <button
                  data-testid="clear-member"
                  onClick={handleClearSelected}
                  style={{
                    position: 'absolute',
                    top: 2,
                    right: 2,
                    bottom: 2,
                    display: 'flex',
                    alignItems: 'center',
                    padding: '6px',
                    background: colors.snow,
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  <HighlightOffIcon fontSize="small" sx={{ color: 'primary.main' }} />
                </button>
              )}
            </Box>
            <Typography variant="caption1" mt={4} mb={0.5} display="block" color="dimgrey">
              {t('organizationEapRequests.form.uploadHeading')}
            </Typography>
            <UploadFormField
              label={t('organizationEapRequests.form.selectFile')}
              accept=".pdf"
              selectedFile={selectedFile}
              onChange={handleSelectFile}
              onReset={handleRemoveFile}
            />
            <Box display="flex" justifyContent="end" mt={{ xs: 4, md: 8 }}>
              <LoadingButton
                variant="contained"
                color="primary"
                type="submit"
                disabled={isSubmitDisabled}
                loading={isLoading}
                data-testid="submit-request"
              >
                {t('organizationEapRequests.form.submit')}
              </LoadingButton>
            </Box>
          </form>
        </FormProvider>
        <MemberSearchModal
          open={openSearchModal}
          title={t('searchMember.modal.titleRequestForm')}
          organizationId={organizationId}
          planIds={idsOfWorkplaceReferralPlan}
          onClose={onClose}
          onSelectMember={handleSelectMember}
        />
      </CardContent>
    </Card>
  )
}
